import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useState } from 'react';
import Label from '../Label';

/**
 * Above this width-to-height ratio a logo is treated as a wordmark rather than an icon.
 * Cruise line logos arrive as both — `cunard.gif` is a wide wordmark, `msccruise.gif` is
 * close to square — and squeezing a wordmark into a 48px circle renders it unreadably small.
 */
const WORDMARK_RATIO = 1.6;

const AvatarHeading = ({
  imageSrc,
  imageTitle,
  title,
  subTitle,
  className,
  children,
  doShowCruiseTourLabel,
}) => {
  const [isWordmark, setIsWordmark] = useState(false);

  /**
   * The API gives no aspect hint and this component renders both shapes, so measure the image.
   *
   * Called from a ref callback as well as onLoad: a cached image is already `complete` by the
   * time React attaches handlers, so onLoad alone never fires and every logo would keep the
   * circle. Measured cruise line thumbs are 90px wide by 12-27px tall (ratios 3.3-7.5), i.e.
   * all wordmarks — they render at 44x13 in a 48px circle, which is the "small and off" look.
   */
  const measure = (img) => {
    if (!img || !img.naturalHeight) return;
    setIsWordmark(img.naturalWidth / img.naturalHeight > WORDMARK_RATIO);
  };

  return (
    <div className={clsx('flex grid-flow-col gap-5 text-left', className)}>
      {/*
        Centred with flex rather than the old `pt-2`, which pushed every logo off centre.
        overflow-hidden keeps a wide logo inside the frame, and object-contain scales any
        aspect ratio without distorting it.
      */}
      <div
        className={clsx(
          'border-2 flex items-center justify-center overflow-hidden flex-shrink-0 bg-white p-1',
          isWordmark ? 'rounded-lg h-12 w-24' : 'rounded-full h-12 w-12',
        )}
      >
        <img
          ref={measure}
          className="max-h-full max-w-full object-contain"
          src={imageSrc}
          title={imageTitle}
          alt={imageTitle}
          onLoad={(e) => measure(e.target)}
        />
      </div>
      <div>
        <div className="font-bold sm:text-sm xl:text-lg">
          {title}
          {doShowCruiseTourLabel && (
            <span className="ml-2">
              <Label text="Cruise Tour" level="themeWithBg" inline />
            </span>
          )}
        </div>
        {subTitle && <div className="sm:text-xs xl:text-sm">{subTitle}</div>}
        <div className="sm:text-xs xl:text-sm flex flex-wrap">{children}</div>
      </div>
    </div>
  );
};

AvatarHeading.propTypes = {
  imageSrc: PropTypes.string,
  imageTitle: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  doShowCruiseTourLabel: PropTypes.bool,
};

AvatarHeading.defaultProps = {
  imageSrc: undefined,
  className: undefined,
  children: undefined,
  doShowCruiseTourLabel: false,
  subTitle: undefined,
};

export default AvatarHeading;
